import React, { useState } from 'react';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { registerUser } from '../services/user';

import { useNavigate } from 'react-router-dom';

import { Modal } from 'react-bootstrap';

import PasswordRequirements from '../components/PasswordRequirements';
import ErrorModal from '../modals/ErrorModal';
import i18n from '../utils/i18n';
import { trackAuthentication } from '../utils/analytics';
import Button from '../components/Button';
import TurnstileComponent from '../components/TurnstileComponent';

type FormValues = {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
};

const Register = () => {
  const navigate = useNavigate();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [loading, setLoading] = useState(false);

  const [passwordRequirementsMet, setPasswordRequirementsMet] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTurnstileSuccess, setIsTurnstileSuccess] = useState(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(i18n.t('CreateAccount.form.errors.emailAddressValidEmail'))
      .required(i18n.t('CreateAccount.form.errors.required')),
    username: Yup.string().required(
      i18n.t('CreateAccount.form.errors.required')
    ),
    password: Yup.string().required(
      i18n.t('CreateAccount.form.errors.required')
    ),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref('password')],
        i18n.t('CreateAccount.form.errors.passwordMustMatch')
      )
      .required(i18n.t('CreateAccount.form.errors.required'))
      .trim(),
  });

  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    message?: string;
  }>({
    isOpen: false,
    message: '',
  });

  const onSubmit = async (values: FormValues) => {
    try {
      setLoading(true);
      await registerUser(
        values.username,
        values.email.toLowerCase(),
        values.password
      );
      // Track successful registration in Google Analytics
      trackAuthentication('register', true);
      setShowConfirmationModal(true);
    } catch (error: any) {
      // Track failed registration
      trackAuthentication('register', false);

      if (error.response?.status === 409) {
        const field = error.response?.data.message.includes('email')
          ? 'e-mail'
          : 'usuário';
        setErrorModal({
          isOpen: true,
          message: `Esse ${field} já é existente. Por favor, tente outro ${field}.`,
        });
        return;
      }
      setErrorModal({
        isOpen: true,
        message: `Ocorreu um problema ao registrar o usuário. Tente novamente, por favor.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const onCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
    navigate('/login');
  };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    isValid,
    errors,
    touched,
    values,
  } = useFormik({
    initialValues: {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit,
    validateOnBlur: false,
    initialErrors: {
      email: i18n.t('CreateAccount.form.errors.required'),
      password: i18n.t('CreateAccount.form.errors.required'),
      confirmPassword: i18n.t('CreateAccount.form.errors.required'),
    },
  });

  return (
    <>
      <div className="auth-container">
        <div className="container-fluid h-100">
          <div className="row h-100 align-items-center justify-content-center">
            <div className="col-12 col-md-8 col-lg-6 col-xl-5">
              <div className="auth-card">
                <div className="auth-logo-container">
                  <img
                    src="assets/images/logo-only.png"
                    className="auth-logo"
                    alt="Silk Secret"
                  />
                  <h1 className="auth-brand-name">silk secret</h1>
                </div>

                <h2 className="auth-title">{i18n.t<string>('CreateAccount.title')}</h2>

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="input-group">
                    <i className="feather-at-sign input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={i18n.t<string>(
                        'CreateAccount.form.username'
                      )}
                      autoCapitalize="none"
                      id="username"
                      onChange={(e) => {
                        setFieldValue('username', e.target.value.toLowerCase());
                      }}
                      onBlur={handleBlur}
                      value={values.username}
                      disabled={loading}
                    />
                    {touched.username && errors.username && (
                      <div className="input-error">
                        <i className="feather-alert-circle"></i>
                        {errors.username}
                      </div>
                    )}
                  </div>

                  <div className="input-group">
                    <i className="ti-email input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={i18n.t<string>('CreateAccount.form.email')}
                      id="email"
                      autoCapitalize="none"
                      inputMode="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      disabled={loading}
                    />
                    {touched.email && errors.email && (
                      <div className="input-error">
                        <i className="feather-alert-circle"></i>
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div className="input-group">
                    <i className="ti-lock input-icon"></i>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      placeholder={i18n.t<string>(
                        'CreateAccount.form.password'
                      )}
                      id="password"
                      autoCapitalize="none"
                      onChange={(e) => {
                        const text = e.target.value;
                        setFieldValue('password', text.trim());
                      }}
                      onBlur={handleBlur}
                      disabled={loading}
                      value={values.password}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i
                        className={`feather-${
                          showPassword ? 'eye' : 'eye-off'
                        }`}
                      ></i>
                    </button>
                    {touched.password && errors.password && (
                      <div className="input-error">
                        <i className="feather-alert-circle"></i>
                        {errors.password}
                      </div>
                    )}
                  </div>

                  <PasswordRequirements
                    password={values.password}
                    onMeetAllRequirements={() => {
                      setPasswordRequirementsMet(true);
                    }}
                    onRequirementsNotMet={() => {
                      setPasswordRequirementsMet(false);
                    }}
                  />

                  <div className="input-group">
                    <i className="ti-lock input-icon"></i>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="form-input"
                      placeholder={i18n.t<string>(
                        'CreateAccount.form.confirmPassword'
                      )}
                      id="confirmPassword"
                      autoCapitalize="none"
                      onChange={(e) => {
                        const text = e.target.value;
                        setFieldValue('confirmPassword', text.trim());
                      }}
                      onBlur={handleBlur}
                      disabled={loading}
                      value={values.confirmPassword}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i
                        className={`feather-${
                          showConfirmPassword ? 'eye' : 'eye-off'
                        }`}
                      ></i>
                    </button>
                    {touched.confirmPassword && errors.confirmPassword && (
                      <div className="input-error">
                        <i className="feather-alert-circle"></i>
                        {errors.confirmPassword}
                      </div>
                    )}
                  </div>

                  <div className="terms-container">
                    <p className="terms-text">
                      {i18n.t<string>('CreateAccount.form.acceptTermsText1')}{' '}
                      <a
                        href="https://silksecret.me/termos"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="terms-link"
                      >
                        {i18n.t<string>('CreateAccount.form.acceptTermsText2')}
                      </a>{' '}
                      {i18n.t<string>('CreateAccount.form.acceptTermsText3')}{' '}
                      <a
                        href="https://silksecret.me/privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="terms-link"
                      >
                        {i18n.t<string>('CreateAccount.form.acceptTermsText4')}
                      </a>{' '}
                      {i18n.t<string>('CreateAccount.form.acceptTermsText5')}
                    </p>
                  </div>

                  {!isTurnstileSuccess && (
                    <div className="d-flex justify-content-center">
                      <TurnstileComponent
                        onSuccess={() => setIsTurnstileSuccess(true)}
                        onError={() => setIsTurnstileSuccess(false)}
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!isValid || !passwordRequirementsMet || !isTurnstileSuccess || loading}
                    className="btn-auth"
                  >
                    {loading ? (
                      <div className="auth-loading">
                        <div className="spinner"></div>
                        <span>{i18n.t<string>('CreateAccount.button')}</span>
                      </div>
                    ) : (
                      i18n.t<string>('CreateAccount.button')
                    )}
                  </button>
                </form>

                <div className="signup-link">
                  {i18n.t<string>('CreateAccount.alreadyHaveAccount')}
                  <a href="/login" style={{ marginLeft: 5 }}>
                    {i18n.t<string>('CreateAccount.login')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => {
          setErrorModal({ isOpen: false });
        }}
      />
      <Modal show={showConfirmationModal} onHide={onCloseConfirmationModal}>
        <Modal.Header
          closeButton
          onClick={onCloseConfirmationModal}
          className="bg-white"
          style={{
            border: 0,
            borderRadius: 0,
            padding: 20,
            borderTopLeftRadius: 10,
            WebkitBorderTopRightRadius: 10,
          }}
        />
        <Modal.Body
          className="bg-white"
          style={{
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          <p className="font-xs text-black">
            {i18n.t<string>('CreateAccount.confirmationMessage', {
              email: values.email,
            })}
          </p>
          <div className="form-group mb-1">
            <Button
              onClick={onCloseConfirmationModal}
              disabled={!isValid}
            >
              {i18n.t<string>('Global.ok')}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Register;